import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import api, { fmtOdds } from "../lib/api";
import { StatBar, ConfBar, UpgradeGate } from "../components/Bits";
import { SearchSuggest, HRLogChart } from "../components/SearchSuggest";
import { BallparkModal } from "../components/BallparkModal";
import { findGameForTeam } from "../lib/ballparks";
import { Star, Wind, Thermometer, MapPin, Crosshair } from "lucide-react";

export function PitchMix({ pa, batterName }) {
  const [side, setSide] = useState("season");
  if (!pa?.mix?.length) {
    return (
      <div className="card-surface rounded-lg p-3.5 sm:p-5" data-testid="pitch-mix-card">
        <h3 className="font-display text-base sm:text-lg font-bold uppercase text-[#a855f7]">Pitch Mix</h3>
        <p className="text-xs text-neutral-500 mt-2">Probable pitcher not announced yet — the pitch mix drops as soon as the arm is confirmed.</p>
      </div>
    );
  }
  const keyMap = { season: "season", lhb: "vs_lhb", rhb: "vs_rhb" };
  const k = keyMap[side];
  const avg3 = (v) => `.${String(Number(v).toFixed(3)).split(".")[1] || "000"}`;
  return (
    <div className="card-surface rounded-lg p-3.5 sm:p-5" data-testid="pitch-mix-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-base sm:text-lg font-bold uppercase text-[#a855f7]">Pitch Mix — {pa.pitcher}{pa.hand ? ` (${pa.hand}HP)` : ""}</h3>
        <div className="flex gap-1">
          {[["season", "Season"], ["lhb", "vs LHB"], ["rhb", "vs RHB"]].map(([v, l]) => (
            <button key={v} onClick={() => setSide(v)} data-testid={`pitchmix-tab-${v}`}
              className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border transition-colors duration-200 ${side === v ? "border-[#a855f7] text-[#a855f7] bg-[#a855f7]/10" : "border-white/15 text-neutral-500 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="mt-3 space-y-2.5">
        {pa.mix.map((m) => (
          <div key={m.code} data-testid={`pitchmix-row-${m.code}`}>
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold text-neutral-200">{m.pitch} <span className="text-neutral-600 font-data">{m.code}</span>
                {m.vulnerable && <span className="ml-2 text-[9px] uppercase tracking-widest font-bold bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded animate-pulse" data-testid={`vulnerable-${m.code}`}>Vulnerable</span>}
              </span>
              <span className="font-data text-[#a855f7] font-bold">{m[k]}%</span>
            </div>
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden mt-1">
              <div className={`h-full rounded-full transition-all duration-500 ${m.vulnerable ? "bg-red-400" : "bg-[#a855f7]"}`} style={{ width: `${Math.min(m[k], 100)}%` }} />
            </div>
            <div className="flex gap-3 text-[10px] text-neutral-500 font-data mt-0.5">
              <span>BA {avg3(m.ba)}</span><span>SLG {avg3(m.slg)}</span><span>Whiff {m.whiff}%</span><span>HH {m.hh}%</span>
            </div>
          </div>
        ))}
      </div>
      {pa.batter_vs?.length > 0 && batterName && (
        <div className="mt-4 border-t border-white/10 pt-3" data-testid="batter-vs-pitch">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">How {batterName.split(" ").slice(-1)[0]} handles these pitches</div>
          <div className="space-y-1">
            {pa.batter_vs.slice(0, 7).map((b) => (
              <div key={b.code} data-testid={`batter-vs-${b.code}`}
                className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded ${b.crushes ? "bg-[#22c55e]/10 border border-[#22c55e]/40" : b.thrown ? "bg-white/5" : "opacity-50"}`}>
                <span className={`flex-1 truncate ${b.crushes ? "text-[#22c55e] font-semibold" : "text-neutral-300"}`}>
                  {b.pitch}{b.crushes ? " — crushes it" : ""}{!b.thrown ? " (not thrown)" : ""}
                </span>
                <span className="font-data text-neutral-500">{b.pa} PA</span>
                <span className="font-data">{avg3(b.ba)}</span>
                <span className="font-data text-neutral-500">K {b.k_pct}%</span>
                <span className={`font-data font-bold ${b.hr > 0 ? "text-[#a855f7]" : "text-neutral-600"}`}>{b.hr} HR</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <p className="text-[10px] text-neutral-600 mt-2">{pa.source === "savant" ? "Real Statcast arsenal + per-pitch results. vs LHB/RHB splits are model estimates." : "Model-estimated mix — Statcast arsenal not available for this arm yet."}</p>
    </div>
  );
}

function Detail({ mlbId }) {
  const qc = useQueryClient();
  const [sprayOpen, setSprayOpen] = useState(false);
  const { data } = useQuery({ queryKey: ["player", mlbId], queryFn: () => api.get(`/players/${mlbId}`).then((r) => r.data) });
  const { data: hrLog } = useQuery({
    queryKey: ["hrLog", String(mlbId)],
    queryFn: () => api.get(`/hr-log/${mlbId}`).then((r) => r.data),
    enabled: !!data && !data.locked,
  });
  const { data: wxData } = useQuery({ queryKey: ["weather"], queryFn: () => api.get("/weather").then((r) => r.data) });
  const { data: wl } = useQuery({ queryKey: ["watchlist"], queryFn: () => api.get("/watchlist").then((r) => r.data) });
  const watched = (wl?.players || []).some((p) => p.mlb_id === Number(mlbId));
  const toggleWatch = useMutation({
    mutationFn: () => (watched ? api.delete(`/watchlist/${mlbId}`) : api.post(`/watchlist/${mlbId}`)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["watchlist"] });
      toast.success(watched ? "Removed from watchlist" : "Added to watchlist");
    },
  });

  if (!data) return <div className="text-neutral-500 font-data text-sm animate-pulse p-6">Loading profile...</div>;
  const p = data.player;
  const sprayGame = findGameForTeam(wxData?.games, p.team_name, p.weather);

  return (
    <div className="space-y-5">
      <div className="card-surface rounded-xl p-4 sm:p-6 flex flex-wrap items-center gap-4 sm:gap-5 gold-glow">
        <img src={p.headshot} alt={p.name} className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover bg-white/5 border-2 border-[#a855f7]/40" />
        <div className="flex-1 min-w-[180px]">
          <h2 className="font-display text-2xl sm:text-3xl font-black uppercase" data-testid="player-detail-name">
            {p.name}
            {p.status && p.status.includes("Injured") && <span className="ml-2 text-xs uppercase font-bold bg-red-500/20 text-red-400 px-2 py-1 rounded align-middle" data-testid="player-il-badge">{p.status}</span>}
          </h2>
          <div className="text-neutral-400 text-xs sm:text-sm">{p.team_name} · {p.position}{p.bats ? ` · Bats ${p.bats}` : ""} · {p.season} season</div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {[[`.${String(p.stats.avg).split(".")[1] || "000"}`, "AVG"], [p.stats.hr, "HR"], [p.stats.ops, "OPS"], [p.stats.pa, "PA"]].map(([v, l]) => (
              <span key={l} className="bg-black/40 border border-white/10 rounded px-2 py-1 font-data text-xs"><b className="text-[#a855f7]">{v}</b> <span className="text-neutral-500">{l}</span></span>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {sprayGame && (
            <button onClick={() => setSprayOpen(true)} data-testid="research-spray-btn"
              className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold px-4 py-2 rounded border border-white/20 text-neutral-300 hover:border-[#a855f7] hover:text-[#a855f7] transition-colors duration-200">
              <Crosshair size={14} /> Spray Chart
            </button>
          )}
          <button onClick={() => toggleWatch.mutate()} data-testid="watchlist-toggle-btn"
            className={`flex items-center gap-2 text-xs uppercase tracking-wider font-bold px-4 py-2 rounded border transition-colors duration-200 ${watched ? "border-[#a855f7] text-[#a855f7] bg-[#a855f7]/10" : "border-white/20 text-neutral-300 hover:border-[#a855f7]"}`}>
            <Star size={14} fill={watched ? "#a855f7" : "none"} /> {watched ? "Watching" : "Watch"}
          </button>
        </div>
      </div>

      {sprayOpen && sprayGame && (
        <BallparkModal game={sprayGame} onClose={() => setSprayOpen(false)}
          initialBatter={{ mlb_id: Number(mlbId), name: p.name, team: p.team, bats: p.bats, headshot: p.headshot }} />
      )}

      {data.locked ? (
        <UpgradeGate feature="Full Research Center (advanced metrics, matchups, splits and odds)" />
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <div className="card-surface rounded-lg p-3.5 sm:p-5 space-y-3 sm:space-y-4">
              <h3 className="font-display text-base sm:text-lg font-bold uppercase text-[#a855f7]">Power Profile</h3>
              {p.last10 && (
                <div className="flex items-center justify-between bg-black/40 border border-white/10 rounded p-2.5">
                  <div>
                    <div className="text-[10px] uppercase text-neutral-500">Last 10 Days</div>
                    <div className="font-data text-sm font-bold" data-testid="last10-line">{p.last10.hr} HR · .{String(p.last10.avg).split(".")[1] || "000"} AVG · {p.last10.games} G</div>
                  </div>
                  <span data-testid="form-chip" className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded ${
                    p.last10.form === "Locked In" ? "bg-[#22c55e]/20 text-[#22c55e]" :
                    p.last10.form === "Weak Spot" ? "bg-red-500/20 text-red-400" :
                    "bg-white/10 text-neutral-400"}`}>
                    {p.last10.form}
                  </span>
                </div>
              )}
              <StatBar label="HR Probability" value={p.stats.hr_probability} max={45} good={20} />
              <StatBar label="Barrel %" value={p.stats.barrel_pct} max={25} good={12} />
              <StatBar label="Hard Hit %" value={p.stats.hard_hit_pct} max={60} good={45} />
              <StatBar label="Fly Ball %" value={p.stats.fly_ball_pct} max={55} good={40} />
              <StatBar label="Pull %" value={p.stats.pull_pct} max={55} good={42} />
            </div>

            <div className="card-surface rounded-lg p-3.5 sm:p-5">
              <h3 className="font-display text-base sm:text-lg font-bold uppercase text-[#a855f7]">Tonight's Matchup</h3>
              <div className="mt-3 space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-neutral-400">Probable pitcher</span><span className="font-semibold">{p.matchup.pitcher === "TBD" ? "TBD — announced closer to game time" : `${p.matchup.pitcher} (${p.matchup.hand}HP)`}</span></div>
                <div className="flex justify-between"><span className="text-neutral-400">ERA</span><span className="font-data">{p.matchup.era}</span></div>
                <div className="flex justify-between"><span className="text-neutral-400">HR/9</span><span className={`font-data ${p.matchup.hr9 >= 1.4 ? "text-[#22c55e]" : ""}`}>{p.matchup.hr9}</span></div>
                <div className="flex justify-between"><span className="text-neutral-400">WHIP</span><span className="font-data">{p.matchup.whip}</span></div>
                <div className="border-t border-white/10 pt-3 space-y-2">
                  <div className="flex items-center gap-2 text-neutral-300"><MapPin size={14} className="text-[#a855f7]" />{p.park.park} <span className="ml-auto font-data">Park {p.park.rating}/10</span></div>
                  <div className="flex items-center gap-2 text-neutral-300"><Thermometer size={14} className="text-[#a855f7]" />{p.weather.temp}°F · {p.weather.conditions}</div>
                  <div className="flex items-center gap-2 text-neutral-300"><Wind size={14} className="text-[#a855f7]" />{p.weather.wind_speed} mph {p.weather.wind_dir}</div>
                </div>
              </div>
              <div className="mt-4 border-t border-white/10 pt-3">
                <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold mb-1">Confidence Meter</div>
                <ConfBar value={p.confidence} />
              </div>
            </div>

            <div className="card-gold border border-[#a855f7]/30 rounded-lg p-3.5 sm:p-5 gold-glow">
              <h3 className="font-display text-base sm:text-lg font-bold uppercase text-[#a855f7]">Vegas vs Model</h3>
              <div className="mt-3 space-y-2 text-sm">
                {p.odds.lines.map((l) => (
                  <div key={l.book} className={`flex justify-between items-center px-2 py-1.5 rounded ${l.book === p.odds.best_book ? "border border-[#a855f7]/50 bg-[#a855f7]/10" : ""}`}>
                    <span className={l.book === p.odds.best_book ? "text-[#a855f7] font-semibold" : "text-neutral-400"}>{l.book}</span>
                    <span className="font-data">{fmtOdds(l.odds)} <span className="text-neutral-600 text-xs">({l.implied}%)</span></span>
                  </div>
                ))}
              </div>
              <div className="mt-4 border-t border-white/10 pt-3 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-neutral-400">Model HR prob</span><span className="font-data">{p.odds.model_prob}%</span></div>
                <div className="flex justify-between"><span className="text-neutral-400">Best implied</span><span className="font-data">{p.odds.best_implied}%</span></div>
                <div className="flex justify-between font-semibold"><span className="text-neutral-300">Value edge</span>
                  <span className={`font-data ${p.odds.edge > 0 ? "text-[#22c55e]" : "text-red-400"}`} data-testid="player-edge">{p.odds.edge > 0 ? "+" : ""}{p.odds.edge}%</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-3 sm:gap-4">
            <PitchMix pa={p.pitch_attack} batterName={p.name} />
            <div className="card-surface rounded-lg p-3.5 sm:p-5" data-testid="hr-log-card">
              <h3 className="font-display text-base sm:text-lg font-bold uppercase text-[#a855f7]">Last 15 Games</h3>
              <div className="mt-3">
                {hrLog ? <HRLogChart log={hrLog} /> : <p className="text-xs text-neutral-500 animate-pulse">Pulling the game log...</p>}
              </div>
              {hrLog?.last15 && (
                <div className="mt-3 overflow-x-auto border-t border-white/10 pt-2">
                  <table className="w-full text-xs sm:text-sm min-w-[420px]" data-testid="hrlog-totals">
                    <thead>
                      <tr className="text-neutral-500 text-[10px] uppercase tracking-wider text-left border-b border-white/10">
                        <th className="py-1.5 pr-3">Span</th><th className="py-1.5 pr-2">G</th><th className="py-1.5 pr-2">AB</th><th className="py-1.5 pr-2">H</th><th className="py-1.5 pr-2">R</th><th className="py-1.5 pr-2">RBI</th><th className="py-1.5 pr-2">K</th><th className="py-1.5 pr-2">HR</th><th className="py-1.5">AVG</th>
                      </tr>
                    </thead>
                    <tbody className="font-data">
                      {[["Last 10 G", hrLog.last10], ["Last 15 G", hrLog.last15]].map(([l, t]) => (
                        <tr key={l} className="border-b border-white/5">
                          <td className="py-1.5 pr-3 font-sans text-neutral-300">{l}</td>
                          <td className="pr-2">{t.games}</td><td className="pr-2">{t.ab}</td><td className="pr-2">{t.h}</td><td className="pr-2">{t.r}</td><td className="pr-2">{t.rbi}</td><td className="pr-2">{t.k}</td>
                          <td className="pr-2 text-[#22c55e] font-bold">{t.hr}</td>
                          <td>.{Number(t.avg).toFixed(3).split(".")[1]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="card-surface rounded-lg p-3.5 sm:p-5">
            <h3 className="font-display text-base sm:text-lg font-bold uppercase text-[#a855f7]">Splits</h3>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm min-w-[520px]">
                <thead>
                  <tr className="text-neutral-500 text-xs uppercase tracking-wider text-left border-b border-white/10">
                    <th className="py-2 pr-4">Split</th><th className="py-2 pr-4">AVG</th><th className="py-2 pr-4">HR</th><th className="py-2 pr-4">OPS</th><th className="py-2">Barrel %</th>
                  </tr>
                </thead>
                <tbody className="font-data">
                  <tr className="border-b border-white/5"><td className="py-2 pr-4 font-sans text-neutral-300">Last 15</td><td>{p.splits.last15.avg}</td><td>{p.splits.last15.hr}</td><td>{p.splits.last15.ops}</td><td>{p.splits.last15.barrel}%</td></tr>
                  <tr className="border-b border-white/5"><td className="py-2 pr-4 font-sans text-neutral-300">Last 30</td><td>{p.splits.last30.avg}</td><td>{p.splits.last30.hr}</td><td>{p.splits.last30.ops}</td><td>{p.splits.last30.barrel}%</td></tr>
                  <tr><td className="py-2 pr-4 font-sans text-neutral-300">vs {p.matchup.pitcher} ({p.splits.vs_pitcher.ab} AB)</td><td>{p.splits.vs_pitcher.avg}</td><td>{p.splits.vs_pitcher.hr}</td><td>{p.splits.vs_pitcher.ops}</td><td>—</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function Research() {
  const { mlbId } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const { data } = useQuery({ queryKey: ["players"], queryFn: () => api.get("/players").then((r) => r.data) });
  const players = data?.players || [];
  const filtered = q.trim()
    ? players.filter((p) => `${p.name} ${p.team} ${p.team_name}`.toLowerCase().includes(q.trim().toLowerCase()))
    : players;
  const active = mlbId || filtered[0]?.mlb_id;

  return (
    <div className="space-y-5">
      <div className="fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">Research <span className="gold-text">Center</span></h1>
        <div className="accent-bar w-32 mt-2 mb-2" />
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xs uppercase tracking-wider font-bold px-3 py-1.5 rounded-full bg-[#a855f7]/15 text-[#a855f7] border border-[#a855f7]/40" data-testid="research-tab-batters">Batters</span>
          <button onClick={() => navigate("/pitchers")} data-testid="research-tab-pitchers"
            className="text-xs uppercase tracking-wider font-bold px-3 py-1.5 rounded-full text-neutral-400 border border-white/15 hover:border-[#a855f7] hover:text-[#a855f7] transition-colors duration-200">
            Pitchers →
          </button>
        </div>
        <p className="text-neutral-500 text-sm mt-2">{players.length} hitters — every batter this season, ranked best to worst daily. Real probable pitchers, live stats, weather and value edges. IL players included.</p>
      </div>
      <div className="grid lg:grid-cols-[260px_1fr] gap-5 fade-up-1">
        <div className="min-w-0">
          <div className="mb-2">
            <SearchSuggest value={q} onChange={setQ} testid="research-search-input"
              options={[...players.map((p) => p.name), ...new Set(players.map((p) => p.team_name))]}
              placeholder="Search player or team..." />
          </div>
          <div className="card-surface rounded-lg p-2 max-h-[36vh] lg:max-h-[70vh] overflow-y-auto">
            {filtered.slice(0, 100).map((p) => (
              <button key={p.mlb_id} onClick={() => navigate(`/research/${p.mlb_id}`)} data-testid={`player-list-${p.mlb_id}`}
                className={`flex items-center gap-2.5 w-full text-left px-2.5 py-2 rounded transition-colors duration-200 ${Number(active) === p.mlb_id ? "bg-[#a855f7]/15 text-[#a855f7]" : "text-neutral-300 hover:bg-white/5"}`}>
                <img src={p.headshot} alt="" className="w-8 h-8 rounded-full object-cover bg-white/5" loading="lazy" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">
                    {p.name}
                    {p.status && p.status.includes("Injured") && <span className="ml-1.5 text-[9px] uppercase font-bold bg-red-500/20 text-red-400 px-1 py-0.5 rounded">IL</span>}
                  </div>
                  <div className="text-[11px] text-neutral-600">{p.team} · conf {p.confidence}</div>
                </div>
              </button>
            ))}
            {filtered.length === 0 && <p className="text-neutral-600 text-xs p-3">No players match "{q}".</p>}
            {filtered.length > 100 && <p className="text-neutral-600 text-[11px] p-2">Showing top 100 — refine your search.</p>}
          </div>
        </div>
        <div className="min-w-0">{active && <Detail mlbId={active} />}</div>
      </div>
    </div>
  );
}
